import React, {
  useState, useContext, useRef, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Truncate from 'react-truncate';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CircularProgress from '@material-ui/core/CircularProgress';
import styled from 'styled-components';
import CloseBtnGrayIcon from 'assets/closeBtnGray.svg';
import { useSelector } from 'react-redux';
import SendBtnIcon from 'assets/sendBtn.svg';
import SendBtnGrayIcon from 'assets/sendBtnGray.svg';
import ErrorIcon from 'assets/errorIcon.svg';
import ErrorHollowIcon from 'assets/errorHollowIcon.svg';
import { selectMessagesByTargetId } from 'models/messages';
import UserAvatar from 'components/UserAvatar';
import { RONGCLOUD_CHATROOM_PREFIX } from 'appenv';
import ChatContext from './ChatContext';
import { UserSessionContext } from '../UserSession';

const Container = styled.div`
  width: ${({ isDesktop }) => (isDesktop ? '60%' : '100%')};
  height: ${({ isDesktop }) => (isDesktop ? '80%' : '100%')};
  background-color: #F0F1F2;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 0 8px 0 rgba(0,0,0, 0.3);
  margin: auto;
`;

const Title = styled.div`
  font-family: 'Lato';
  background-color: #FFF;
  color: #484848;
  margin: 0;
  height: 44px;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  h3 {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 70%;
  }
`;

const ErrorMsgContainer = styled.div`
  background-color: #edd5d1;
  display: flex;
  height: 40px;
  padding: 11px 16px;
  color: #484848;
  font-size: 14px;
  img {
    width: 18px;
    height: 18px;
    margin-right: 16px;
  }
`;

const ChatBody = styled.div`
  box-sizing: border-box;
  padding: 0 20px;
  margin: 10px 0 0;
  height: ${({ errorMsg }) => (errorMsg ? 'calc(100% - 146px)' : 'calc(100% - 106px)')};
  overflow-y: scroll;
`;

const StyledDate = styled.div`
  text-align: center;
  span {
    color: #fff;
    border-radius: 2px;
    font-size: 12px;
    padding: 4px;
    background-color: #c9c9c9;
  }
`;

const StyledInput = styled.input`
  background: rgba(255, 255, 255, 0.5);
  outline: 0;
  border: 1px solid #DBDEE2;
  padding: 0 5px;
  border-radius: 15px;
  margin: 10px 16px;
  height: 33px;
  width: calc(100% - 50px);
  box-sizing: border-box;
  font-size: 16px;
`;

const SendBtn = styled.img`
  width: 26px;
  height: 26px;
  margin-right: 20px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background-color: #F8F8F8;
`;

const MessageContainer = styled.div`
  text-decoration: none;
  color: #3498DB;
  clear: both;
  display: flex;
  margin: 24px 0;

  &.self {
    text-align: right;
    flex-direction: row-reverse;
    .text-column .msg-group {
      justify-content: flex-end;
    }
  }

  .text-column {
    margin: 0 10px;
    display: block;
    max-width: 50%;
    word-break: break-word;

    .msg-group {
      display: flex;
      justify-content: flex-start;

      img {
        margin: 0 10px;
        cursor: pointer;
      }
    }

    .send-time {
      color: #A2AAB3;
      font-size: 13px;
    }
  }
`;

const Message = styled.div`
  display: inline-flex;
  position: relative;
  padding: 16px;
  font-size: 14px;
  border-radius: 8px;
  margin: 10px 0;
  width: fit-content;
  text-align: left;
  background-color: ${({ isSelfSent }) => (isSelfSent ? '#5C8DF3' : '#FFF')};
  color: ${({ isSelfSent }) => (isSelfSent ? '#fff' : '#484848')};
`;

const UserInfo = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #484848;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
`;

const UserTag = styled.span`
  border-radius: 2px;
  background-color: #ffb7b5;
  color: #fff;
  font-size: 12px;
  padding: 2px 5px;
  margin-left: 9px;
`;

const UserDescription = styled.div`
  color: #484848;
  font-size: 12px;
  margin-top: 8px;
`;

const CloseBtn = styled.div`
  position: absolute;
  height: 16px;
  width: 16px;
  right: 17px;
  top: 14px;
  cursor: pointer;

  img {
    height: 16px;
    width: 16px;
  }
`;

const LoadingIndicatorContainer = styled.div`
  width: 100%;
  padding: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MoreMessagesBtn = styled.div`
  text-align: center;
  letter-spacing: -0.408px;
  color: #5C8DF3;
  cursor: pointer;
  margin-bottom: 16px;
`;

const ChatWindow = ({
  joiningChat,
  status,
  chatRoomTitle,
  sendMessage,
  resendMessage,
  getHistoryMsg,
  hasMoreMsg,
}) => {
  const { userSession } = useContext(UserSessionContext);
  const { leaveChatRoom, targetId } = useContext(ChatContext);
  const [messageValue, setMessageValue] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const messageRef = useRef();
  const textInputRef = useRef();
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const EnterBtnCode = 13;
  const messages = useSelector(selectMessagesByTargetId(targetId));
  const [autoscroll, setAutoscroll] = useState(true);
  const [firstMsgId, setFirstMsgId] = useState(null);

  const getTrackId = () => {
    let trackId = targetId;
    if (targetId === `${RONGCLOUD_CHATROOM_PREFIX}_hall`) {
      trackId = 'Hall';
    } else if (targetId === `${RONGCLOUD_CHATROOM_PREFIX}_announcement`) {
      trackId = 'Announcement';
    } else {
      const result = targetId.split(`${RONGCLOUD_CHATROOM_PREFIX}_booth_`);
      const boothId = result[1];
      if (boothId) {
        trackId = boothId;
      }
    }
    return trackId;
  };

  const handleMessageSend = useCallback(() => {
    if (messageValue) {
      const trackingId = getTrackId();
      window.trackingEvent('TextMessage_Send', 'Public Chatroom', `${trackingId} - ${chatRoomTitle}`);
      sendMessage(messageValue);
      setMessageValue('');
      textInputRef.current.focus();
    }
  }, [messageValue, sendMessage]);

  useEffect(() => {
    if (joiningChat) return;
    if (status === 'offline') {
      setErrorMsg('Network unavailable, check network');
    } else if (status === 'disconnected') {
      setErrorMsg('Cannot connect to the chatroom');
    } else {
      setErrorMsg('');
    }
  }, [status]);

  useEffect(() => {
    if (autoscroll) {
      messageRef.current.scrollTop = messageRef.current.scrollHeight;
    } else if (firstMsgId) {
      const targetComponent = document.getElementById(firstMsgId);
      messageRef.current.scrollTop = targetComponent.offsetTop - targetComponent.clientHeight;
      setFirstMsgId(null);
    }
  }, [messages]);

  const onScroll = (e) => {
    setAutoscroll(e.target.scrollHeight <= e.target.scrollTop + e.target.clientHeight);
  };

  const fetchMoreMessages = () => {
    setFirstMsgId((messages[0] || {}).id);
    getHistoryMsg(null);
  };

  const onKeydown = useCallback((event) => {
    switch (event.keyCode) {
      case EnterBtnCode:
        handleMessageSend();
        break;
      default:
    }
  }, [handleMessageSend]);

  const displaySentTime = (timestamp) => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString();
    const minutes = date.getMinutes().toString();
    const formatH = hours.length === 1 ? `0${hours}` : hours;
    const formatM = minutes.length === 1 ? `0${minutes}` : minutes;
    return `${formatH}:${formatM}`;
  };

  const displayDateTag = (date) => {
    const today = moment();
    if (today.isSame(date, 'day')) {
      return 'Today';
    }
    return date.format('ddd, MMM D');
  };

  let dateFlag;
  return (
    <Container isDesktop={isDesktop}>
      <Title>
        <h3>
          {chatRoomTitle}
        </h3>
        <CloseBtn onClick={leaveChatRoom}>
          <img src={CloseBtnGrayIcon} alt="close" />
        </CloseBtn>
      </Title>
      {
        errorMsg && (
          <ErrorMsgContainer>
            <img src={ErrorIcon} alt="error" />
            {errorMsg}
          </ErrorMsgContainer>
        )
      }
      <ChatBody ref={messageRef} errorMsg={errorMsg} onScroll={onScroll}>
        {
          joiningChat ? (
            <LoadingIndicatorContainer>
              <CircularProgress />
            </LoadingIndicatorContainer>
          ) : (
            <>
              { hasMoreMsg && <MoreMessagesBtn onClick={fetchMoreMessages}>Load Earlier Messages</MoreMessagesBtn>}
              {
                messages.map((msg) => {
                  const msgDate = moment(msg.sentTime);
                  const isRenderDate = msgDate.format('DD/MM/YYYY') !== dateFlag;
                  dateFlag = msgDate.format('DD/MM/YYYY');
                  return (
                    <React.Fragment key={msg.id}>
                      {(isRenderDate ? <StyledDate><span>{displayDateTag(msgDate)}</span></StyledDate> : null)}
                      <MessageContainer className={msg.senderUserId === userSession.rongcloudUserId ? 'self' : ''} id={msg.id}>
                        <UserAvatar name={msg.content.user.name} />
                        <div className="text-column">
                          <UserInfo>
                            {msg.content.user.name}
                            {
                              msg.content.user.tag && (
                                <UserTag>{msg.content.user.tag}</UserTag>
                              )
                            }
                          </UserInfo>
                          <UserDescription>
                            <Truncate lines={2} ellipsis="...">
                              {msg.content.user.description}
                            </Truncate>
                          </UserDescription>
                          <div className="msg-group">
                            {(msg.sentFailed && <img role="presentation" src={ErrorHollowIcon} alt="Send failed" onClick={() => resendMessage(msg)} />)}
                            <Message isSelfSent={msg.senderUserId === userSession.rongcloudUserId}>
                              {
                                msg.messageType === window.RongIMClient.MessageType.TextMessage && (
                                  window.RongIMLib.RongIMEmoji.symbolToEmoji(msg.content.content)
                                )
                              }
                            </Message>
                          </div>
                          <div className="send-time">
                            {displaySentTime(msg.sentTime)}
                          </div>
                        </div>
                      </MessageContainer>
                    </React.Fragment>
                  );
                })
              }
            </>
          )
        }
      </ChatBody>
      <FooterContainer>
        <StyledInput ref={textInputRef} disabled={joiningChat} value={messageValue} onChange={(e) => setMessageValue(e.target.value)} onKeyDown={onKeydown} />
        <SendBtn disabled={!messageValue} src={messageValue ? SendBtnIcon : SendBtnGrayIcon} onClick={handleMessageSend} alt="send" />
      </FooterContainer>
    </Container>
  );
};

ChatWindow.propTypes = {
  joiningChat: PropTypes.bool.isRequired,
  status: PropTypes.string.isRequired,
  chatRoomTitle: PropTypes.string.isRequired,
  sendMessage: PropTypes.func.isRequired,
  resendMessage: PropTypes.func.isRequired,
  getHistoryMsg: PropTypes.func.isRequired,
  hasMoreMsg: PropTypes.bool.isRequired,
};

export default ChatWindow;
